import type { QuestLogEntry } from '../../shared/protocol.ts';
import { $, $$, el } from '../common/dom.ts';
import { withIcon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';
import { creatureText } from '../../shared/game-config.ts';

type QuestStatus = QuestLogEntry['status'];

const EMPTY: Readonly<Record<QuestStatus, string>> = {
  active: 'Aucune quête en cours. Parlez aux habitants marqués d\'un « ! ».',
  completed: 'Aucune quête terminée pour l\'instant.',
};

/** Journal de quêtes : onglets « En cours » (avec progression) et « Terminées ». */
export class QuestLog extends Modal {
  private readonly list = $('.quest-list', this.root);
  private readonly filters = $$<HTMLButtonElement>('[data-quest-filter]', this.root);
  private readonly load: () => Promise<QuestLogEntry[]>;
  private quests: QuestLogEntry[] = [];
  /** Onglet ouvert, gardé d'une ouverture du journal à l'autre. */
  private filter: QuestStatus = 'active';

  constructor(load: () => Promise<QuestLogEntry[]>) {
    super('#quests');
    this.load = load;
    $('#btn-quests').addEventListener('click', () => this.toggle());
    for (const button of this.filters) {
      button.addEventListener('click', () => {
        this.filter = button.dataset.questFilter === 'completed' ? 'completed' : 'active';
        this.render();
      });
    }
  }

  protected override reveal(): Promise<void> {
    return this.show();
  }

  async show(): Promise<void> {
    this.open();
    const current = this.startLoad();
    this.list.replaceChildren(el('p', { className: 'muted', textContent: 'Chargement…' }));
    const quests = await this.load().catch(() => null);
    if (!current()) return;
    if (!quests) {
      this.list.replaceChildren(el('p', { className: 'muted', textContent: 'Journal indisponible.' }));
      return;
    }
    this.quests = quests;
    this.render();
  }

  private render(): void {
    const labels: Record<QuestStatus, string> = { active: 'En cours', completed: 'Terminées' };
    for (const button of this.filters) {
      const status: QuestStatus = button.dataset.questFilter === 'completed' ? 'completed' : 'active';
      const count = this.quests.filter((quest) => quest.status === status).length;
      button.textContent = `${labels[status]} (${count})`;
      button.classList.toggle('active', status === this.filter);
      button.setAttribute('aria-selected', String(status === this.filter));
    }
    const shown = this.quests.filter((quest) => quest.status === this.filter);
    this.list.replaceChildren(...(shown.length ? shown.map((quest) => this.entry(quest)) : [el('p', { className: 'empty', textContent: EMPTY[this.filter] })]));
  }

  private entry(quest: QuestLogEntry): HTMLElement {
    const done = quest.status === 'completed';
    const bar = el('div', { className: 'bar quest-bar' }, el('div'));
    (bar.firstElementChild as HTMLElement).style.width = `${(100 * quest.progress) / quest.target}%`;
    return el('article', { className: `quest-entry ${done ? 'done' : ''}` },
      el('header', {},
        el('strong', {}, ...withIcon(done ? 'check' : 'quests', quest.name)),
        el('span', { className: 'quest-count', textContent: done ? `Terminée${quest.timesCompleted > 1 ? ` ×${quest.timesCompleted}` : ''}` : `${quest.progress}/${quest.target}` })),
      // Étapes faites (barrées), puis l'étape en cours.
      ...(quest.steps > 1 ? [el('ol', { className: 'quest-steps' }, ...quest.doneSteps.map((step) => el('li', { className: 'done', textContent: step })),
        ...(done ? [] : [el('li', { className: 'current', textContent: `${quest.objective} (étape ${quest.step + 1}/${quest.steps})` })]))]
        : done ? [] : [el('p', { className: 'quest-objective' }, ...withIcon('target', quest.objective))]),
      // Coop : ce joueur est plus loin que son partenaire et l'attend ; ce qui est montré est l'étape du partenaire.
      ...(quest.catchingUp && !done ? [el('p', { className: 'quest-catchup' }, ...withIcon('users',
        `Rattrapage : vous attendez ${quest.catchingUp.partner}${quest.steps > 1 ? ` (vous en êtes à l'étape ${quest.catchingUp.step + 1}/${quest.steps})` : ''}. Ce que vous faites compte pour lui d'abord.`))] : []),
      el('p', { textContent: creatureText(quest.description) }),
      ...(done ? [] : [bar]),
      el('small', { textContent: [quest.giver ? `Confiée par ${quest.giver}` : '', `${quest.steps > 1 && !done ? 'Récompense de l\'étape' : 'Récompense'} : ${creatureText(quest.rewards)}`, this.repeatNote(quest)].filter(Boolean).join(' · ') }));
  }

  /** Quête répétable ou quotidienne : quand elle revient. */
  private repeatNote({ repeat, status, availableAgain }: QuestLogEntry): string {
    if (repeat === 'once') return '';
    const kind = repeat === 'daily' ? 'Quotidienne' : 'Répétable';
    if (status !== 'completed') return kind;
    return `${kind} : ${availableAgain ? 'de nouveau disponible auprès de son donneur' : 'revient demain'}`;
  }
}
